import type { ReactNode } from "react";
import { NavLink, Navigate, Outlet, useLocation, useSearchParams } from "react-router-dom"
import { useTenantPathFn } from "@/lib/tenant";
import { BUCKET_TABS, SECONDARY_TABS, postureIndexTarget, type PostureTab } from "./postureTabs";

/**
 * Posture tabs.
 *
 * These are route-driven NavLinks, not Radix Tabs: each tab is a real URL, so
 * it is deep-linkable, the back button undoes a tab change, and every one of
 * them is guarded server-side by its own route rather than by a client-side
 * panel switch.
 *
 * Two groups (D27): the four buckets first, in the overview's order —
 * Critical & high, Medium & low, AI best practice, Microsoft Secure Score —
 * then, set apart, All findings, Microsoft baseline, Manual attestations and
 * Exceptions. Every tab is always present; a switched-off framework's tab says
 * "Switched off" (D25) rather than disappearing.
 *
 * The ?run= parameter is carried across tabs, because "the posture of this
 * run" is the thing being navigated, not eight unrelated screens.
 */

/**
 * @param children the tab body. Omitted, the shell renders an <Outlet/>, which
 * is how src/routes.tsx uses it: as the LAYOUT route the five tabs nest under.
 * The wrapper form remains for tests and for any page that wants the shell
 * around content it renders itself. Nothing else puts the heading, the tab
 * bar or the help button on screen, so a tab registered outside this route
 * loses all three — src/features/posture/__tests__/PostureShell.routing.test.tsx
 * fails when that happens.
 */
export function PostureShell({ children }: { children?: ReactNode }) {
  const resolve = useTenantPathFn()
  const [searchParams] = useSearchParams()
  const run = searchParams.get("run")
  const suffix = run ? `?run=${encodeURIComponent(run)}` : ""

  const tabLink = (tab: PostureTab) => (
    <li key={tab.to}>
      <NavLink
        to={resolve(`${tab.to}${suffix}`)}
        data-testid={`posture-tab-${tab.id}`}
        className={({ isActive }) =>
          [
            "focus-ring inline-block border-b-2 px-3 py-2 text-sm font-medium",
            isActive
              ? "border-brand text-brand-ink"
              : "border-transparent text-ink-3 hover:border-border hover:text-ink-2",
          ].join(" ")
        }
      >
        {tab.label}
      </NavLink>
    </li>
  )

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold text-ink">Security posture</h1>
      </div>
        <p className="text-sm text-ink-3">
          {run ? (
            <>
              Reporting on run <span className="font-mono text-xs">{run}</span>.
            </>
          ) : (
            "Reporting on the most recent scan run."
          )}
        </p>
      </header>

      <nav aria-label="Posture views" className="border-b border-border">
        <div className="-mb-px flex flex-wrap items-end gap-x-4 gap-y-1">
          <ul className="flex flex-wrap gap-1" aria-label="Score buckets" data-testid="posture-tabs-buckets">
            {BUCKET_TABS.map(tabLink)}
          </ul>
          <span aria-hidden="true" className="mb-2 hidden h-5 w-px bg-border sm:block" />
          <ul
            className="flex flex-wrap gap-1"
            aria-label="Other posture views"
            data-testid="posture-tabs-secondary"
          >
            {SECONDARY_TABS.map(tabLink)}
          </ul>
        </div>
      </nav>

      {children ?? <Outlet />}
    </div>
  )
}

/**
 * /posture itself is not a tab. It redirects — keeping the query string — to
 * the tab a `?tab=` names, to All findings when it carries a findings filter
 * (the old All findings tab lived here, and links still send `?state=…`), and
 * otherwise to the default tab, Critical & high. See postureIndexTarget.
 */
export function PostureIndexRedirect() {
  const resolve = useTenantPathFn()
  const { search } = useLocation()
  return <Navigate to={resolve(postureIndexTarget(search))} replace />
}
